import { AfterViewInit, Component, EventEmitter, forwardRef, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';

/**
 * An on/off switch, with ngModel or a form control:
 *
 *     <gains-switch formControlName="enabled" size="small" ariaLabel="Enabled"></gains-switch>
 *
 * Disabled by its form (`control.disable()`, `[disabled]` with ngModel) or by `disabledState`: either one disables it.
 */
@Component({
  selector: 'gains-switch',
  standalone: false,
  template: `<nz-switch [ngModel]="checked" (ngModelChange)="onToggle($event)" [nzDisabled]="disabled" [nzSize]="size" [nzLoading]="loading" [nzId]="inputId ?? null"></nz-switch>`,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GainsSwitchComponent), multi: true }]
})
export class GainsSwitchComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @ViewChild(NzSwitchComponent) private readonly _switch?: NzSwitchComponent;

  @Input() size: 'default' | 'small' = 'default';
  /** Disabled apart from its form, e.g. while saving. */
  @Input() disabledState = false;
  /** Shows a spinner in the handle, e.g. while the change is saved. */
  @Input() loading = false;
  /** The id of its button, for a `<label for>`. */
  @Input() inputId?: string;
  /** Names the switch for screen readers when it has no label. */
  @Input() ariaLabel?: string;
  /** The new state, as the user switched it. */
  @Output() checkedChange = new EventEmitter<boolean>();

  checked = false;
  formDisabled = false;
  /** Disabled by its form or by `disabledState`. */
  disabled = false;
  private onChange: (checked: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnChanges(): void {
    this.disabled = this.disabledState || this.formDisabled;
    this.describe();
  }

  ngAfterViewInit(): void {
    this.describe();
  }

  writeValue(value: unknown): void {
    this.checked = !!value;
    this.describe();
  }

  registerOnChange(fn: (checked: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.disabled = this.disabledState || this.formDisabled;
  }

  onToggle(checked: boolean): void {
    this.checked = checked;
    this.describe();
    this.onChange(checked);
    this.onTouched();
    this.checkedChange.emit(checked);
  }

  /** What a screen reader needs on ng-zorro's button: a switch, its name and its state. */
  private describe(): void {
    const button: HTMLElement | undefined = this._switch?.switchElement?.nativeElement;
    if (!button) return;
    button.setAttribute('role', 'switch');
    button.setAttribute('aria-checked', String(this.checked));
    if (this.ariaLabel) {
      button.setAttribute('aria-label', this.ariaLabel);
    } else {
      button.removeAttribute('aria-label');
    }
  }
}
